import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Client as NesClient, type NesError } from '@hapi/nes/lib/client';
import {
  ConfirmedSampleNumbering,
  EnsureSampleSheetResponse,
  PartialVolumeNormalisationConfirmation,
  SampleMetadataLimitationsConfirmation,
  ServerMsg,
  ServerRowSnapshot,
  SheetPatchField,
  SheetPatchOp,
  zPartialVolumeNormalisationConfirmation,
  zSampleMetadataLimitationsConfirmation,
  zServerMsg,
} from '@resistapp/common/api-types';
import {
  SampleSheetDraftRow,
  RowValidationIssues as SharedRowValidationIssues,
} from '@resistapp/common/sample-sheet-server';
import { getServerUrl } from '@resistapp/common/utils';
import { isEqual } from 'lodash';

import { storedToken } from '../components/shared/local-storage';
import { resistomapApiClient } from '../utils/fetch';
import { createCellKey } from '../views/sample-sheet-view/sample-sheet-cell-keys';

type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'offline';

type PatchInput = {
  rowId: string;
  field: SheetPatchField;
  value: unknown;
  previousValue?: unknown;
};

export type SheetRowState = SampleSheetDraftRow & {
  validation: RowValidationIssues;
  rowVersion: number;
  rowIndex: number;
  isSubmittedSample?: boolean;
  submittedEditsDirty?: boolean;
  hasSpecificCoordinates?: boolean;
};

type RowValidationIssues = SharedRowValidationIssues & {
  isSubmittedSample?: string[];
  submittedEditsDirty?: string[];
  hasSpecificCoordinates?: string[];
};

type SubmissionSummary = {
  submitted: number;
  locked: number;
  at: number;
};

type ServerDeltaMessage = Extract<ServerMsg, { type: 'delta' }>;

export type SubmitSheetResult =
  | {
      status: 'submitted';
      submitted: number;
      locked: number;
    }
  | PartialVolumeNormalisationConfirmation
  | SampleMetadataLimitationsConfirmation;

export type ResubmitSheetResult =
  | {
      status: 'resubmitted';
      resubmitted: number;
      locked: number;
    }
  | PartialVolumeNormalisationConfirmation
  | SampleMetadataLimitationsConfirmation;

export type SampleSheetSubmissionConfirmationOptions = {
  metadataLimitationsConfirmationToken?: string;
  confirmProceedWithPartialVolumeNormalisation?: boolean;
  // Bound to what the submitter actually confirmed, so the server can refuse a numbering that moved since then.
  expectedSampleNumbering?: ConfirmedSampleNumbering;
};

export type ServerSheetContextValue = {
  ready: boolean;
  loading: boolean;
  sheetId: string | null;
  sheetVersion: number;
  rows: SheetRowState[];
  meta: Record<string, unknown>;
  connection: { state: ConnectionState; lagMs?: number };
  pendingCells: ReadonlySet<string>;
  correctedCells: ReadonlySet<string>;
  queuePatch: (ops: PatchInput[]) => void;
  createRows: (count: number) => Promise<void>;
  deleteRows: (rowIds: string[]) => Promise<void>;
  submit: (rowIds: string[], options?: SampleSheetSubmissionConfirmationOptions) => Promise<SubmitSheetResult>;
  beginSampleEdits: () => Promise<void>;
  cancelSampleEdits: () => Promise<void>;
  resubmitSampleEdits: (
    rowIds: string[],
    options?: SampleSheetSubmissionConfirmationOptions,
  ) => Promise<ResubmitSheetResult>;
  resync: () => Promise<void>;
  clearCorrectedCells: (cellKeys: string[]) => void;
  lastSubmission: SubmissionSummary | null;
  error: string | null;
  genePanelId: number | null;
  labWorkbookId: string | null;
  labSheetUrl: string | null;
};

export const ServerSheetContext = createContext<ServerSheetContextValue | undefined>(undefined);

type ProviderProps = {
  organisationId?: number | null;
  sampleSheetId?: string | null;
  children: ReactNode;
};

const FLUSH_DEBOUNCE_MS = 25;
const FLUSH_MAX_OPS = 250;

export function ServerSheetProvider({ organisationId = null, sampleSheetId = null, children }: ProviderProps) {
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [sheetVersion, setSheetVersion] = useState<number>(0);
  const [meta, setMeta] = useState<Record<string, unknown>>({});
  const [rows, setRows] = useState<SheetRowState[]>([]);
  const [genePanelId, setGenePanelId] = useState<number | null>(null);
  const [labWorkbookId, setLabWorkbookId] = useState<string | null>(null);
  const [labSheetUrl, setLabSheetUrl] = useState<string | null>(null);
  const [connection, setConnection] = useState<{ state: ConnectionState; lagMs?: number }>({ state: 'offline' });
  const [pendingCells, setPendingCellsState] = useState<Set<string>>(new Set());
  const [correctedCells, setCorrectedCellsState] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [lastSubmission, setLastSubmission] = useState<SubmissionSummary | null>(null);

  const sheetIdRef = useRef<string | null>(null);
  const sheetVersionRef = useRef<number>(0);
  const rowsRef = useRef<Map<string, SheetRowState>>(new Map());
  const pendingCellsRef = useRef<Set<string>>(pendingCells);
  const correctedCellsRef = useRef<Set<string>>(correctedCells);
  const clientRef = useRef<NesClient | null>(null);
  const isConnectedRef = useRef(false);
  const pendingOpsRef = useRef<SheetPatchOp[]>([]);
  const flushTimerRef = useRef<number | null>(null);
  const isFlushingRef = useRef(false);
  const optimisticByCellRef = useRef<Map<string, { txnId: string; optimisticValue: unknown; previousValue: unknown }>>(
    new Map(),
  );
  const txnToCellsRef = useRef<Map<string, string[]>>(new Map());
  const txnSendTimeRef = useRef<Map<string, number>>(new Map());
  const resyncInFlightRef = useRef(false);

  useEffect(() => {
    sheetIdRef.current = sheetId;
  }, [sheetId]);

  useEffect(() => {
    sheetVersionRef.current = sheetVersion;
  }, [sheetVersion]);

  useEffect(() => {
    pendingCellsRef.current = pendingCells;
  }, [pendingCells]);

  useEffect(() => {
    correctedCellsRef.current = correctedCells;
  }, [correctedCells]);

  const updateSheetVersion = useCallback((nextVersion: number) => {
    if (!Number.isFinite(nextVersion)) {
      return;
    }
    sheetVersionRef.current = nextVersion;
    setSheetVersion(nextVersion);
  }, []);

  const resetState = useCallback(() => {
    rowsRef.current = new Map();
    setRows([]);
    setPendingCellsState(new Set());
    pendingCellsRef.current = new Set();
    setCorrectedCellsState(new Set());
    correctedCellsRef.current = new Set();
    optimisticByCellRef.current.clear();
    txnToCellsRef.current.clear();
    txnSendTimeRef.current.clear();
    pendingOpsRef.current = [];
    if (flushTimerRef.current != null) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, []);

  const normalizeMeta = useCallback((value: unknown) => {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }, []);

  const normalizeGenePanelId = useCallback((value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    return null;
  }, []);

  const normalizeValidation = useCallback((value: unknown): RowValidationIssues => {
    if (!value || typeof value !== 'object') {
      return {};
    }
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      return {};
    }
    const result: RowValidationIssues = {};
    entries.forEach(([field, maybeIssues]) => {
      if (!Array.isArray(maybeIssues)) {
        return;
      }
      const filtered = maybeIssues.filter((issue): issue is string => typeof issue === 'string');
      if (filtered.length > 0) {
        result[field as keyof SampleSheetDraftRow] = filtered;
      }
    });
    return result;
  }, []);

  const toRowState = useCallback(
    (snapshot: ServerRowSnapshot): SheetRowState => {
      const basePartial = {
        rowId: snapshot.rowId,
        isReadOnly: snapshot.isReadOnly,
        sampleId: snapshot.sampleId,
        samplingPointId: snapshot.samplingPointId,
        rowVersion: snapshot.rowVersion,
        rowIndex: Number.isFinite(snapshot.rowIndex) ? snapshot.rowIndex : Number.MAX_SAFE_INTEGER,
        validation: normalizeValidation(snapshot.validation),
      };
      const base = basePartial as SheetRowState;

      const dataEntries =
        snapshot.data && typeof snapshot.data === 'object'
          ? Object.entries(snapshot.data as Record<string, unknown>)
          : [];
      dataEntries.forEach(([key, value]) => {
        (base as Record<string, unknown>)[key] = value ?? null;
      });

      const derivedEntries =
        snapshot.derived && typeof snapshot.derived === 'object'
          ? Object.entries(snapshot.derived as Record<string, unknown>)
          : [];
      derivedEntries.forEach(([key, value]) => {
        (base as Record<string, unknown>)[key] = value ?? null;
      });

      return base;
    },
    [normalizeValidation],
  );

  const sortRows = useCallback((map: Map<string, SheetRowState>): SheetRowState[] => {
    return Array.from(map.values()).sort((left, right) => {
      const leftIsSubmittedSample = Boolean(left.isSubmittedSample);
      const rightIsSubmittedSample = Boolean(right.isSubmittedSample);
      if (leftIsSubmittedSample !== rightIsSubmittedSample) {
        return leftIsSubmittedSample ? -1 : 1;
      }

      const leftIndex = Number.isFinite(left.rowIndex) ? left.rowIndex : Number.MAX_SAFE_INTEGER;
      const rightIndex = Number.isFinite(right.rowIndex) ? right.rowIndex : Number.MAX_SAFE_INTEGER;
      if (leftIndex !== rightIndex) {
        return leftIndex - rightIndex;
      }

      return left.rowId.localeCompare(right.rowId);
    });
  }, []);

  const cleanupRowArtifacts = useCallback((rowIds: string[]) => {
    if (rowIds.length === 0) {
      return;
    }

    const nextPending = new Set(pendingCellsRef.current);
    const nextCorrected = new Set(correctedCellsRef.current);

    const optimistic = optimisticByCellRef.current;
    const txns = txnToCellsRef.current;

    rowIds.forEach(rowId => {
      const prefix = `${rowId}::`;

      for (const key of Array.from(optimistic.keys())) {
        if (key.startsWith(prefix)) {
          optimistic.delete(key);
        }
      }

      for (const cellKey of Array.from(nextPending)) {
        if (cellKey.startsWith(prefix)) {
          nextPending.delete(cellKey);
        }
      }

      for (const cellKey of Array.from(nextCorrected)) {
        if (cellKey.startsWith(prefix)) {
          nextCorrected.delete(cellKey);
        }
      }

      for (const [txnId, cells] of Array.from(txns.entries())) {
        const filtered = cells.filter(cell => !cell.startsWith(prefix));
        if (filtered.length === 0) {
          txns.delete(txnId);
          txnSendTimeRef.current.delete(txnId);
        } else if (filtered.length !== cells.length) {
          txns.set(txnId, filtered);
        }
      }
    });

    if (!setsAreEqual(nextPending, pendingCellsRef.current)) {
      pendingCellsRef.current = nextPending;
      setPendingCellsState(nextPending);
    }
    if (!setsAreEqual(nextCorrected, correctedCellsRef.current)) {
      correctedCellsRef.current = nextCorrected;
      setCorrectedCellsState(nextCorrected);
    }
  }, []);

  const updateLag = useCallback((durationMs: number) => {
    if (!Number.isFinite(durationMs) || durationMs < 0) {
      return;
    }
    setConnection(prev => {
      const previousLag = prev.lagMs;
      const nextLag = previousLag == null ? Math.round(durationMs) : Math.round(previousLag * 0.6 + durationMs * 0.4);
      if (prev.state === 'connected' && previousLag === nextLag) {
        return prev;
      }
      return { ...prev, lagMs: nextLag };
    });
  }, []);

  const updatePendingAfterDelta = useCallback((cellUpdates: Array<{ cellKey: string; newValue: unknown }>) => {
    if (cellUpdates.length === 0) {
      return;
    }

    const nextPending = new Set(pendingCellsRef.current);
    const nextCorrected = new Set(correctedCellsRef.current);

    cellUpdates.forEach(({ cellKey, newValue }) => {
      const optimistic = optimisticByCellRef.current.get(cellKey);
      if (!optimistic) {
        return;
      }
      optimisticByCellRef.current.delete(cellKey);

      nextPending.delete(cellKey);

      if (!isEqual(optimistic.optimisticValue, newValue)) {
        if (!nextCorrected.has(cellKey)) {
          nextCorrected.add(cellKey);
        }
      } else {
        nextCorrected.delete(cellKey);
      }
    });

    if (!setsAreEqual(nextPending, pendingCellsRef.current)) {
      pendingCellsRef.current = nextPending;
      setPendingCellsState(nextPending);
    }
    if (!setsAreEqual(nextCorrected, correctedCellsRef.current)) {
      correctedCellsRef.current = nextCorrected;
      setCorrectedCellsState(nextCorrected);
    }
  }, []);

  const applySnapshot = useCallback(
    (snapshot: {
      rows: ServerRowSnapshot[];
      sheetVersion: number;
      meta: Record<string, unknown>;
      genePanelId: number | null;
      labWorkbookId?: string | null;
      labSheetUrl?: string | null;
    }) => {
      const nextMap = new Map<string, SheetRowState>();
      snapshot.rows.forEach(row => {
        nextMap.set(row.rowId, toRowState(row));
      });

      rowsRef.current = nextMap;
      setRows(sortRows(nextMap));
      setMeta(normalizeMeta(snapshot.meta));
      setGenePanelId(normalizeGenePanelId(snapshot.genePanelId));
      if (Object.prototype.hasOwnProperty.call(snapshot, 'labWorkbookId')) {
        setLabWorkbookId(snapshot.labWorkbookId ?? null);
      }
      if (Object.prototype.hasOwnProperty.call(snapshot, 'labSheetUrl')) {
        setLabSheetUrl(snapshot.labSheetUrl ?? null);
      }
      setReady(true);
      updateSheetVersion(snapshot.sheetVersion);
      const nextPending = new Set<string>();
      setPendingCellsState(nextPending);
      pendingCellsRef.current = nextPending;
      const nextCorrected = new Set<string>();
      setCorrectedCellsState(nextCorrected);
      correctedCellsRef.current = nextCorrected;
      optimisticByCellRef.current.clear();
      pendingOpsRef.current = [];
      txnToCellsRef.current.clear();
      txnSendTimeRef.current.clear();
      if (flushTimerRef.current != null) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    },
    [normalizeGenePanelId, normalizeMeta, sortRows, toRowState, updateSheetVersion],
  );

  const applyDelta = useCallback(
    (
      nextVersion: number,
      changes: ServerDeltaMessage['changes'],
      metaUpdate?: Record<string, unknown>,
      genePanelIdUpdate?: number | null,
    ) => {
      if (metaUpdate) {
        setMeta(normalizeMeta(metaUpdate));
      }
      if (genePanelIdUpdate !== undefined) {
        setGenePanelId(normalizeGenePanelId(genePanelIdUpdate));
      }

      if (changes.length === 0) {
        updateSheetVersion(nextVersion);
        return;
      }

      const currentMap = new Map(rowsRef.current);
      const touchedRows: string[] = [];
      const removedRows: string[] = [];
      const touchedCells: Array<{ cellKey: string; newValue: unknown }> = [];

      changes.forEach(change => {
        if (change.deleted) {
          if (currentMap.delete(change.rowId)) {
            removedRows.push(change.rowId);
          }
          return;
        }

        const previous = currentMap.get(change.rowId);
        const base: SheetRowState = previous
          ? { ...previous, validation: { ...previous.validation } }
          : toRowState({
              rowId: change.rowId,
              isReadOnly: Boolean(change.isReadOnly),
              sampleId: change.sampleId ?? null,
              samplingPointId: change.samplingPointId ?? null,
              rowVersion: typeof change.rowVersion === 'number' ? change.rowVersion : 0,
              rowIndex:
                typeof change.rowIndex === 'number' && Number.isFinite(change.rowIndex)
                  ? change.rowIndex
                  : Number.MAX_SAFE_INTEGER,
              data: {},
              derived: {},
              validation: change.validation ?? {},
            });

        if (typeof change.rowVersion === 'number') {
          base.rowVersion = change.rowVersion;
        }
        if (typeof change.rowIndex === 'number') {
          base.rowIndex = change.rowIndex;
        }
        if (change.sampleId !== undefined) {
          base.sampleId = change.sampleId ?? null;
        }
        if (change.samplingPointId !== undefined) {
          base.samplingPointId = change.samplingPointId ?? null;
        }
        if (typeof change.isReadOnly === 'boolean') {
          base.isReadOnly = change.isReadOnly;
        }
        if (change.validation) {
          base.validation = normalizeValidation(change.validation);
        }

        const fields = new Set<string>();

        if (change.set) {
          Object.entries(change.set).forEach(([key, value]) => {
            fields.add(key);
            (base as Record<string, unknown>)[key] = value ?? null;
          });
        }

        if (change.clear) {
          change.clear.forEach((field: string) => {
            fields.add(field);
            (base as Record<string, unknown>)[field] = null;
          });
        }

        currentMap.set(change.rowId, base);
        touchedRows.push(change.rowId);

        fields.forEach(field => {
          const cellKey = createCellKey(change.rowId, field);
          touchedCells.push({ cellKey, newValue: (base as Record<string, unknown>)[field] });
        });
      });

      rowsRef.current = currentMap;
      if (touchedRows.length > 0 || removedRows.length > 0) {
        setRows(sortRows(currentMap));
      }

      if (removedRows.length > 0) {
        cleanupRowArtifacts(removedRows);
      }

      if (touchedCells.length > 0) {
        updatePendingAfterDelta(touchedCells);
      }

      updateSheetVersion(nextVersion);
    },
    [
      cleanupRowArtifacts,
      normalizeGenePanelId,
      normalizeMeta,
      normalizeValidation,
      sortRows,
      toRowState,
      updatePendingAfterDelta,
      updateSheetVersion,
    ],
  );

  const handleAck = useCallback(
    (ackTxnIds: string[] | undefined) => {
      if (!ackTxnIds || ackTxnIds.length === 0) {
        return;
      }

      const nextPending = new Set(pendingCellsRef.current);

      ackTxnIds.forEach(txnId => {
        const cells = txnToCellsRef.current.get(txnId);
        if (typeof txnSendTimeRef.current.get(txnId) === 'number') {
          const startedAt = txnSendTimeRef.current.get(txnId);
          txnSendTimeRef.current.delete(txnId);
          if (typeof startedAt === 'number') {
            updateLag(Date.now() - startedAt);
          }
        }
        txnToCellsRef.current.delete(txnId);
        if (!cells) {
          return;
        }
        cells.forEach(cellKey => {
          nextPending.delete(cellKey);
        });
      });

      if (!setsAreEqual(nextPending, pendingCellsRef.current)) {
        pendingCellsRef.current = nextPending;
        setPendingCellsState(nextPending);
      }
    },
    [updateLag],
  );

  // Some ws commands (e.g. begin/cancel/resubmit submitted edits) can result in a single delta.
  // If that delta is missed, the client won't observe a version jump to trigger requestResync(),
  // so we fall back to an explicit resync based on the command response sheetVersion.
  const getWsMessagePayload = useCallback((value: unknown): unknown => {
    if (!value || typeof value !== 'object') {
      return value;
    }
    const record = value as Record<string, unknown>;
    if (!Object.prototype.hasOwnProperty.call(record, 'payload')) {
      return value;
    }
    const keys = Object.keys(record);
    if (keys.length !== 1) {
      return value;
    }
    return record.payload;
  }, []);

  const getSheetVersionFromWsResponse = useCallback(
    (value: unknown): number | null => {
      const payload = getWsMessagePayload(value);
      if (!payload || typeof payload !== 'object') {
        return null;
      }
      const sheetVer = (payload as Record<string, unknown>).sheetVersion;
      if (typeof sheetVer !== 'number' || !Number.isFinite(sheetVer)) {
        return null;
      }
      return sheetVer;
    },
    [getWsMessagePayload],
  );

  const waitForSheetVersionAtLeast = useCallback(async (targetVersion: number, timeoutMs: number): Promise<boolean> => {
    const deadline = Date.now() + timeoutMs;

    const tick = async (): Promise<boolean> => {
      if (sheetVersionRef.current >= targetVersion) {
        return true;
      }
      if (Date.now() >= deadline) {
        return sheetVersionRef.current >= targetVersion;
      }
      await new Promise<void>(resolve => {
        window.setTimeout(resolve, 25);
      });
      return await tick();
    };

    return await tick();
  }, []);

  const resync = useCallback(async () => {
    if (!clientRef.current || !sheetIdRef.current) {
      return;
    }
    try {
      await clientRef.current.message({
        type: 'resync',
        sheetId: sheetIdRef.current,
        fromVersion: sheetVersionRef.current,
      });
      setError(null);
    } catch (err) {
      const normalized = toError(err);
      setError(normalized.message);
      throw normalized;
    }
  }, []);

  const requestResync = useCallback(() => {
    if (resyncInFlightRef.current) {
      return;
    }
    resyncInFlightRef.current = true;
    void (async () => {
      try {
        await resync();
      } catch {
        // Errors propagate via setError; swallow to avoid unhandled rejections.
      } finally {
        resyncInFlightRef.current = false;
      }
    })();
  }, [resync]);

  const processServerMessage = useCallback(
    (raw: unknown) => {
      const parsed = zServerMsg.safeParse(raw);
      if (!parsed.success) {
        return;
      }

      const message = parsed.data;
      switch (message.type) {
        case 'init':
          if (sheetIdRef.current && message.sheetId !== sheetIdRef.current) {
            return;
          }
          applySnapshot({
            rows: message.rows,
            sheetVersion: message.sheetVersion,
            meta: message.meta,
            genePanelId: message.genePanelId,
            labWorkbookId: message.labWorkbookId ?? null,
            labSheetUrl: message.labSheetUrl ?? null,
          });
          setError(null);
          break;
        case 'delta':
          if (sheetIdRef.current && message.sheetId !== sheetIdRef.current) {
            return;
          }
          if (message.sheetVersion <= sheetVersionRef.current) {
            return;
          }
          if (message.sheetVersion > sheetVersionRef.current + 1) {
            requestResync();
            return;
          }
          applyDelta(
            message.sheetVersion,
            message.changes,
            message.meta,
            Object.prototype.hasOwnProperty.call(message, 'genePanelId') ? message.genePanelId ?? null : undefined,
          );
          break;
        case 'ack':
          if (sheetIdRef.current && message.sheetId !== sheetIdRef.current) {
            return;
          }
          handleAck(message.ackTxnIds);
          break;
        case 'error':
          setError(message.message);
          break;
        case 'submitted':
          if (sheetIdRef.current && message.sheetId !== sheetIdRef.current) {
            return;
          }
          setLastSubmission({ submitted: message.submitted, locked: message.locked, at: Date.now() });
          break;
        default:
          break;
      }
    },
    [applyDelta, applySnapshot, handleAck, requestResync],
  );

  useEffect(() => {
    let cancelled = false;

    async function ensureSheet() {
      const targetSheetId = typeof sampleSheetId === 'string' && sampleSheetId.length > 0 ? sampleSheetId : null;

      if (organisationId == null) {
        setSheetId(null);
        sheetIdRef.current = null;
        setMeta({});
        setGenePanelId(null);
        setLabWorkbookId(null);
        setLabSheetUrl(null);
        updateSheetVersion(0);
        setReady(false);
        resetState();
        setConnection({ state: 'offline' });
        if (targetSheetId) {
          setError('Organisation identifier is required to load this sample sheet.');
        }
        return;
      }

      const token = storedToken();
      if (!token) {
        setSheetId(null);
        sheetIdRef.current = null;
        setMeta({});
        setGenePanelId(null);
        setLabWorkbookId(null);
        setLabSheetUrl(null);
        updateSheetVersion(0);
        setReady(false);
        setConnection({ state: 'offline' });
        resetState();
        setError('Authentication required');
        return;
      }

      setLoading(true);
      setConnection(prev => ({ state: 'connecting', lagMs: prev.lagMs }));

      try {
        const query = targetSheetId ? `?sheetId=${encodeURIComponent(targetSheetId)}` : '';
        const response = await resistomapApiClient.get<EnsureSampleSheetResponse>(
          `/admin/organisation/${organisationId}/sample-sheet${query}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) {
          return;
        }

        const data = response.data;
        setSheetId(data.sheetId);
        sheetIdRef.current = data.sheetId;
        setMeta(normalizeMeta(data.meta));
        setGenePanelId(normalizeGenePanelId(data.genePanelId));
        setLabWorkbookId(typeof data.labWorkbookId === 'string' ? data.labWorkbookId : null);
        setLabSheetUrl(typeof data.labSheetUrl === 'string' ? data.labSheetUrl : null);
        updateSheetVersion(data.sheetVersion);
        setError(null);
        setReady(false);
        setLastSubmission(null);
        resetState();
      } catch (err) {
        if (cancelled) {
          return;
        }
        const normalized = toError(err);
        setError(normalized.message);
        setSheetId(null);
        sheetIdRef.current = null;
        setConnection({ state: 'offline' });
        setGenePanelId(null);
        setLabWorkbookId(null);
        setLabSheetUrl(null);
        resetState();
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void ensureSheet();

    return () => {
      cancelled = true;
    };
  }, [normalizeGenePanelId, normalizeMeta, organisationId, resetState, sampleSheetId, updateSheetVersion]);

  const flushOps = useCallback(async () => {
    if (isFlushingRef.current) {
      return;
    }
    if (!isConnectedRef.current || !clientRef.current || !sheetIdRef.current) {
      return;
    }
    if (pendingOpsRef.current.length === 0) {
      return;
    }

    isFlushingRef.current = true;
    const ops = pendingOpsRef.current;
    pendingOpsRef.current = [];

    try {
      const result: unknown = await clientRef.current.message({
        type: 'patch',
        sheetId: sheetIdRef.current,
        ops,
      });
      const payload = getWsMessagePayload(result);
      if (payload && typeof payload === 'object') {
        const record = payload as Record<string, unknown>;
        const ackCandidate = record.ackTxnIds;
        if (Array.isArray(ackCandidate)) {
          const ackTxnIds = ackCandidate.filter((value): value is string => typeof value === 'string');
          if (ackTxnIds.length > 0) {
            handleAck(ackTxnIds);
          }
        }
        const nextVersion = record.sheetVersion;
        if (typeof nextVersion === 'number') {
          updateSheetVersion(nextVersion);
        }
      }
    } catch (err) {
      pendingOpsRef.current = ops.concat(pendingOpsRef.current);
      setError(toError(err).message);
    } finally {
      isFlushingRef.current = false;
      const shouldSchedule = pendingOpsRef.current.length > 0 && isConnectedRef.current;
      if (shouldSchedule) {
        const timer = flushTimerRef.current;
        if (timer != null) {
          window.clearTimeout(timer);
        }
        flushTimerRef.current = window.setTimeout(() => {
          flushTimerRef.current = null;
          void flushOps();
        }, FLUSH_DEBOUNCE_MS);
      }
    }
  }, [getWsMessagePayload, handleAck, updateSheetVersion]);

  const scheduleFlush = useCallback(
    (force = false) => {
      if (force) {
        if (flushTimerRef.current != null) {
          window.clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        void flushOps();
        return;
      }

      if (flushTimerRef.current != null) {
        return;
      }

      flushTimerRef.current = window.setTimeout(() => {
        flushTimerRef.current = null;
        void flushOps();
      }, FLUSH_DEBOUNCE_MS);
    },
    [flushOps],
  );

  const queuePatch = useCallback(
    (inputs: PatchInput[]) => {
      if (!sheetIdRef.current || inputs.length === 0) {
        return;
      }

      const ops: SheetPatchOp[] = [];
      const nextPending = new Set(pendingCellsRef.current);

      inputs.forEach(input => {
        const current = rowsRef.current.get(input.rowId);
        if (!current) {
          return;
        }

        const baseRowVersion = current.rowVersion;
        const txnId = generateClientTxnId();
        const value = input.value === undefined || input.value === '' ? null : input.value;
        const rawPreviousValue = Object.prototype.hasOwnProperty.call(input, 'previousValue')
          ? input.previousValue
          : (current as Record<string, unknown>)[input.field];
        const previousValue = rawPreviousValue ?? null;

        const op: SheetPatchOp = {
          rowId: input.rowId,
          field: input.field,
          value,
          baseRowVersion,
          clientTxnId: txnId,
          previousValue,
        };
        ops.push(op);

        const cellKey = createCellKey(input.rowId, String(input.field));
        optimisticByCellRef.current.set(cellKey, {
          txnId,
          optimisticValue: value,
          previousValue,
        });
        txnToCellsRef.current.set(txnId, [cellKey]);
        txnSendTimeRef.current.set(txnId, Date.now());

        nextPending.add(cellKey);
      });

      if (ops.length === 0) {
        return;
      }

      if (!setsAreEqual(nextPending, pendingCellsRef.current)) {
        pendingCellsRef.current = nextPending;
        setPendingCellsState(nextPending);
      }

      pendingOpsRef.current = pendingOpsRef.current.concat(ops);
      if (pendingOpsRef.current.length >= FLUSH_MAX_OPS) {
        scheduleFlush(true);
      } else {
        scheduleFlush(false);
      }
    },
    [scheduleFlush],
  );

  const createRows = useCallback(async (count: number) => {
    if (!clientRef.current || !sheetIdRef.current) {
      throw new Error('Not connected');
    }
    try {
      await clientRef.current.message({ type: 'createRows', sheetId: sheetIdRef.current, count });
      setError(null);
    } catch (err) {
      const normalized = toError(err);
      setError(normalized.message);
      throw normalized;
    }
  }, []);

  const deleteRows = useCallback(async (rowIds: string[]) => {
    if (!clientRef.current || !sheetIdRef.current) {
      throw new Error('Not connected');
    }
    if (rowIds.length === 0) {
      return;
    }
    try {
      await clientRef.current.message({ type: 'deleteRows', sheetId: sheetIdRef.current, rowIds });
      setError(null);
    } catch (err) {
      const normalized = toError(err);
      setError(normalized.message);
      throw normalized;
    }
  }, []);

  const submit = useCallback(
    async (rowIds: string[], options?: SampleSheetSubmissionConfirmationOptions): Promise<SubmitSheetResult> => {
      if (!clientRef.current || !sheetIdRef.current) {
        throw new Error('Not connected');
      }
      if (!Array.isArray(rowIds) || rowIds.length === 0) {
        throw new Error('No rows selected for submission');
      }
      const uniqueRowIds = Array.from(new Set(rowIds.filter(id => typeof id === 'string' && id.length > 0)));
      if (uniqueRowIds.length === 0) {
        throw new Error('No rows selected for submission');
      }
      try {
        const result: unknown = await clientRef.current.message({
          type: 'submit',
          sheetId: sheetIdRef.current,
          rowIds: uniqueRowIds,
          metadataLimitationsConfirmationToken: options?.metadataLimitationsConfirmationToken,
          confirmProceedWithPartialVolumeNormalisation: options?.confirmProceedWithPartialVolumeNormalisation,
          expectedSampleNumbering: options?.expectedSampleNumbering,
        });
        const payload = getWsMessagePayload(result);
        const parsedMetadataConfirmation = zSampleMetadataLimitationsConfirmation.safeParse(payload);
        if (parsedMetadataConfirmation.success) {
          setError(null);
          return parsedMetadataConfirmation.data;
        }
        const parsedConfirmation = zPartialVolumeNormalisationConfirmation.safeParse(payload);
        if (parsedConfirmation.success) {
          setError(null);
          return parsedConfirmation.data;
        }
        if (payload && typeof payload === 'object') {
          const record = payload as Record<string, unknown>;
          const submitted = record.submitted;
          const locked = record.locked;
          if (typeof submitted === 'number' && typeof locked === 'number') {
            setLastSubmission({ submitted, locked, at: Date.now() });
            setError(null);
            return {
              status: 'submitted',
              submitted,
              locked,
            };
          }
        }
        throw new Error('Submit response was missing summary fields.');
      } catch (err) {
        const normalized = toError(err);
        setError(normalized.message);
        throw normalized;
      }
    },
    [getWsMessagePayload],
  );

  const beginSampleEdits = useCallback(async () => {
    if (!clientRef.current || !sheetIdRef.current) {
      throw new Error('Not connected');
    }
    try {
      const response: unknown = await clientRef.current.message({
        type: 'beginSampleEdits',
        sheetId: sheetIdRef.current,
      });
      setError(null);
      const targetSheetVersion = getSheetVersionFromWsResponse(response);
      if (targetSheetVersion == null) {
        return;
      }

      const didObserveUpdate = await waitForSheetVersionAtLeast(targetSheetVersion, 250);
      if (didObserveUpdate) {
        return;
      }
      if (pendingCellsRef.current.size > 0 || pendingOpsRef.current.length > 0) {
        return;
      }

      await resync();
      await waitForSheetVersionAtLeast(targetSheetVersion, 1000);
    } catch (err) {
      const normalized = toError(err);
      setError(normalized.message);
      throw normalized;
    }
  }, [getSheetVersionFromWsResponse, resync, waitForSheetVersionAtLeast]);

  const cancelSampleEdits = useCallback(async () => {
    if (!clientRef.current || !sheetIdRef.current) {
      throw new Error('Not connected');
    }
    try {
      const response: unknown = await clientRef.current.message({
        type: 'cancelSampleEdits',
        sheetId: sheetIdRef.current,
      });
      setError(null);
      const targetSheetVersion = getSheetVersionFromWsResponse(response);
      if (targetSheetVersion == null) {
        return;
      }
      const didObserveUpdate = await waitForSheetVersionAtLeast(targetSheetVersion, 250);
      if (didObserveUpdate) {
        return;
      }
      if (pendingCellsRef.current.size > 0 || pendingOpsRef.current.length > 0) {
        return;
      }
      await resync();
      await waitForSheetVersionAtLeast(targetSheetVersion, 1000);
    } catch (err) {
      const normalized = toError(err);
      setError(normalized.message);
      throw normalized;
    }
  }, [getSheetVersionFromWsResponse, resync, waitForSheetVersionAtLeast]);

  const resubmitSampleEdits = useCallback(
    async (rowIds: string[], options?: SampleSheetSubmissionConfirmationOptions): Promise<ResubmitSheetResult> => {
      if (!clientRef.current || !sheetIdRef.current) {
        throw new Error('Not connected');
      }
      if (!Array.isArray(rowIds) || rowIds.length === 0) {
        throw new Error('No rows selected for resubmission');
      }
      const uniqueRowIds = Array.from(new Set(rowIds.filter(id => typeof id === 'string' && id.length > 0)));
      if (uniqueRowIds.length === 0) {
        throw new Error('No rows selected for resubmission');
      }
      try {
        const response: unknown = await clientRef.current.message({
          type: 'resubmitSampleEdits',
          sheetId: sheetIdRef.current,
          rowIds: uniqueRowIds,
          metadataLimitationsConfirmationToken: options?.metadataLimitationsConfirmationToken,
          confirmProceedWithPartialVolumeNormalisation: options?.confirmProceedWithPartialVolumeNormalisation,
        });
        const payload = getWsMessagePayload(response);
        const parsedMetadataConfirmation = zSampleMetadataLimitationsConfirmation.safeParse(payload);
        if (parsedMetadataConfirmation.success) {
          setError(null);
          return parsedMetadataConfirmation.data;
        }
        const parsedConfirmation = zPartialVolumeNormalisationConfirmation.safeParse(payload);
        if (parsedConfirmation.success) {
          setError(null);
          return parsedConfirmation.data;
        }

        setError(null);
        const targetSheetVersion = getSheetVersionFromWsResponse(response);
        if (targetSheetVersion != null) {
          const didObserveUpdate = await waitForSheetVersionAtLeast(targetSheetVersion, 250);
          if (!didObserveUpdate && pendingCellsRef.current.size === 0 && pendingOpsRef.current.length === 0) {
            await resync();
            await waitForSheetVersionAtLeast(targetSheetVersion, 1000);
          }
        }

        if (payload && typeof payload === 'object') {
          const record = payload as Record<string, unknown>;
          const resubmitted = record.resubmitted;
          const submitted = record.submitted;
          const locked = record.locked;
          const resubmittedCount =
            typeof resubmitted === 'number' ? resubmitted : typeof submitted === 'number' ? submitted : null;
          if (resubmittedCount != null && typeof locked === 'number') {
            return {
              status: 'resubmitted',
              resubmitted: resubmittedCount,
              locked,
            };
          }
        }

        const responseShape =
          payload && typeof payload === 'object'
            ? Object.keys(payload as Record<string, unknown>).join(', ')
            : typeof payload;
        throw new Error(`Resubmission response was missing summary fields (shape: ${responseShape}).`);
      } catch (err) {
        const normalized = toError(err);
        setError(normalized.message);
        throw normalized;
      }
    },
    [getSheetVersionFromWsResponse, getWsMessagePayload, resync, waitForSheetVersionAtLeast],
  );

  const clearCorrectedCells = useCallback((cellKeys: string[]) => {
    if (cellKeys.length === 0) {
      return;
    }
    const next = new Set(correctedCellsRef.current);
    cellKeys.forEach(key => {
      next.delete(key);
    });
    if (!setsAreEqual(next, correctedCellsRef.current)) {
      correctedCellsRef.current = next;
      setCorrectedCellsState(next);
    }
  }, []);

  useEffect(() => {
    if (!sheetId) {
      if (clientRef.current) {
        const existingClient = clientRef.current;
        clientRef.current = null;
        void existingClient.disconnect();
      }
      return;
    }

    const token = storedToken();
    if (!token) {
      setConnection({ state: 'offline' });
      return;
    }

    const wsUrl = toWsUrl(getSampleSheetWsHttpUrl());
    const client = new NesClient(wsUrl);
    clientRef.current = client;
    setConnection(prev => ({ state: 'connecting', lagMs: prev.lagMs }));

    let cancelled = false;
    const subscriptionPath = `/ws/sample-sheets/${sheetId}`;

    client.onConnect = () => {
      if (cancelled) {
        return;
      }
      isConnectedRef.current = true;
      setConnection(prev => ({ state: 'connected', lagMs: prev.lagMs }));
      if (pendingOpsRef.current.length > 0) {
        scheduleFlush(true);
      }
    };

    client.onDisconnect = (willReconnect: boolean) => {
      isConnectedRef.current = false;
      if (cancelled) {
        return;
      }
      setConnection(prev => ({ state: willReconnect ? 'reconnecting' : 'offline', lagMs: prev.lagMs }));
    };

    client.onError = (err: NesError) => {
      if (cancelled) {
        return;
      }
      setError(toError(err).message);
    };

    client.onUpdate = (message: unknown) => {
      if (cancelled) {
        return;
      }
      processServerMessage(message);
    };

    void (async () => {
      try {
        await client.connect({
          auth: { headers: { Authorization: `Bearer ${token}` } },
          reconnect: true,
          delay: 500,
          maxDelay: 4000,
          retries: Infinity,
        });
        await client.subscribe(subscriptionPath, (message: unknown) => {
          if (cancelled) {
            return;
          }
          processServerMessage(message);
        });
        await client.message({ type: 'subscribe', sheetId });
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- guard against late rejections after cleanup
        if (cancelled) {
          return;
        }
        setError(toError(err).message);
        setConnection(prev => ({ state: 'offline', lagMs: prev.lagMs }));
      }
    })();

    return () => {
      cancelled = true;
      isConnectedRef.current = false;
      if (flushTimerRef.current != null) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      void client.unsubscribe(subscriptionPath).catch(() => undefined);
      void client.disconnect().catch(() => undefined);
      if (clientRef.current === client) {
        clientRef.current = null;
      }
    };
  }, [processServerMessage, sheetId]);

  const contextValue = useMemo<ServerSheetContextValue>(
    () => ({
      ready,
      loading,
      sheetId,
      sheetVersion,
      rows,
      meta,
      connection,
      pendingCells,
      correctedCells,
      queuePatch,
      createRows,
      deleteRows,
      submit,
      beginSampleEdits,
      cancelSampleEdits,
      resubmitSampleEdits,
      resync,
      clearCorrectedCells,
      lastSubmission,
      error,
      genePanelId,
      labWorkbookId,
      labSheetUrl,
    }),
    [
      clearCorrectedCells,
      connection,
      correctedCells,
      createRows,
      beginSampleEdits,
      cancelSampleEdits,
      deleteRows,
      error,
      genePanelId,
      labWorkbookId,
      labSheetUrl,
      lastSubmission,
      loading,
      meta,
      pendingCells,
      queuePatch,
      ready,
      resync,
      resubmitSampleEdits,
      rows,
      sheetId,
      sheetVersion,
      submit,
    ],
  );

  return <ServerSheetContext.Provider value={contextValue}>{children}</ServerSheetContext.Provider>;
}

function setsAreEqual<T>(left: Set<T>, right: Set<T>): boolean {
  if (left.size !== right.size) {
    return false;
  }
  return Array.from(left).every(value => right.has(value));
}

function getSampleSheetWsHttpUrl(): string {
  const baseUrl = getServerUrl().replace(/\/$/, '');
  return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
}

function toWsUrl(httpUrl: string): `ws://${string}` | `wss://${string}` {
  if (httpUrl.startsWith('https://')) {
    return `wss://${httpUrl.slice('https://'.length)}`;
  }
  if (httpUrl.startsWith('http://')) {
    return `ws://${httpUrl.slice('http://'.length)}`;
  }
  if (httpUrl.startsWith('ws://') || httpUrl.startsWith('wss://')) {
    return httpUrl as `ws://${string}` | `wss://${string}`;
  }
  return `ws://${httpUrl}`;
}

function generateClientTxnId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  return template.replace(/[xy]/g, character => {
    const random = (Math.random() * 16) | 0;
    if (character === 'x') {
      return random.toString(16);
    }
    return ((random & 0x3) | 0x8).toString(16);
  });
}

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'string') {
    return new Error(error);
  }
  return new Error('Unexpected error');
}
